import React, { useState, useEffect } from 'react';
import {
  Asset,
  SignalType,
  SignalDirection,
  SIGNAL_TYPE_LABELS,
  DIRECTION_LABELS,
  SUBSCRIPTION_LIMITS,
  SUBSCRIPTION_PLANS,
} from '@/api/types';
import { AssetSearch } from './AssetSearch';
import { useAssets, useTelegram } from '@/hooks';
import { useAuthStore } from '@/store/authStore';
import { isValidSignalValue } from '@/utils/validation';

export interface SignalFormData {
  asset: Asset | null;
  quoteCurrency: string;
  type: SignalType;
  value: string;
  direction: SignalDirection[];
  repeat: boolean;
}

interface SignalFormProps {
  initialData?: Partial<SignalFormData>;
  onSubmit: (data: SignalFormData) => void;
  isLoading?: boolean;
  submitLabel?: string;
}

export const SignalForm: React.FC<SignalFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
  submitLabel = 'Create Signal',
}) => {
  const [formData, setFormData] = useState<SignalFormData>({
    asset: initialData?.asset || null,
    quoteCurrency: initialData?.quoteCurrency || 'USDT',
    type: initialData?.type || 'price_threshold',
    value: initialData?.value || '',
    direction: initialData?.direction || ['above'],
    repeat: initialData?.repeat ?? false,
  });
  const [error, setError] = useState<string | null>(null);

  const { quoteCurrencies } = useAssets();
  const { user } = useAuthStore();
  const { impactOccurred } = useTelegram();

  // Get subscription limits - plan comes with format like "cb_299_1m:android" so split by ":"
  const rawPlan = user?.subscription?.plan || 'free';
  const plan = rawPlan.split(':')[0]; // Extract base plan (e.g., "cb_299_1m" from "cb_299_1m:android")
  const limits = SUBSCRIPTION_LIMITS[plan] || SUBSCRIPTION_LIMITS.free;
  const allowedCurrencies = limits.currencies;
  const isMasterPlan = plan === SUBSCRIPTION_PLANS.MASTER;

  // Filter available currencies based on subscription
  const availableCurrencies = (quoteCurrencies || []).filter(
    (c) => allowedCurrencies.includes(c.symbol) || isMasterPlan
  );

  useEffect(() => {
    // Reset direction for absolute_change
    if (formData.type === 'absolute_change') {
      setFormData((prev) => ({ ...prev, direction: ['none'] }));
    } else if (formData.direction.includes('none')) {
      setFormData((prev) => ({ ...prev, direction: ['above'] }));
    }
  }, [formData.type]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.asset) {
      setError('Please select an asset');
      return;
    }

    if (!isValidSignalValue(formData.value, formData.type)) {
      setError(
        formData.type === 'percent_change'
          ? 'Percentage must be between 0.01 and 100'
          : 'Please enter a valid positive number'
      );
      return;
    }

    impactOccurred('medium');
    onSubmit(formData);
  };

  const toggleDirection = (dir: SignalDirection) => {
    if (formData.type === 'absolute_change') return;

    const current = formData.direction;

    if (current.includes(dir)) {
      // Don't allow removing if it's the only one
      if (current.length === 1) return;
      setFormData((prev) => ({
        ...prev,
        direction: current.filter((d) => d !== dir),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        direction: [...current, dir],
      }));
    }
    impactOccurred('light');
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
      {/* Asset Search */}
      <AssetSearch
        selectedAsset={formData.asset}
        onSelect={(asset) => setFormData((prev) => ({ ...prev, asset }))}
      />

      {/* Quote Currency */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--tg-theme-text-color)',
          }}
        >
          Quote Currency
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {availableCurrencies.map((currency) => (
            <button
              key={currency.id}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, quoteCurrency: currency.symbol }));
                impactOccurred('light');
              }}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '15px',
                fontWeight: 500,
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor:
                  formData.quoteCurrency === currency.symbol
                    ? 'var(--tg-theme-button-color)'
                    : 'var(--tg-theme-secondary-bg-color)',
                color:
                  formData.quoteCurrency === currency.symbol
                    ? 'var(--tg-theme-button-text-color)'
                    : 'var(--tg-theme-text-color)',
              }}
            >
              {currency.symbol}
            </button>
          ))}
        </div>
        {!isMasterPlan && (
          <p
            style={{
              fontSize: '12px',
              color: 'var(--tg-theme-hint-color)',
              marginTop: '6px',
            }}
          >
            Upgrade to Pro for all currencies
          </p>
        )}
      </div>

      {/* Signal Type */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--tg-theme-text-color)',
          }}
        >
          Alert Type
        </label>
        <select
          value={formData.type}
          onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as SignalType }))}
          style={{
            width: '100%',
            padding: '14px 16px',
            fontSize: '16px',
            border: '1px solid var(--tg-theme-hint-color)',
            borderRadius: '12px',
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            color: 'var(--tg-theme-text-color)',
            outline: 'none',
          }}
        >
          {(Object.keys(SIGNAL_TYPE_LABELS) as SignalType[]).map((type) => (
            <option key={type} value={type}>
              {SIGNAL_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </div>

      {/* Value */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--tg-theme-text-color)',
          }}
        >
          {formData.type === 'percent_change' ? 'Percentage (%)' : 'Value'}
        </label>
        <input
          type="number"
          inputMode="decimal"
          step="any"
          placeholder={formData.type === 'percent_change' ? 'e.g., 5' : 'e.g., 100'}
          value={formData.value}
          onChange={(e) => setFormData((prev) => ({ ...prev, value: e.target.value }))}
          style={{
            width: '100%',
            padding: '14px 16px',
            fontSize: '16px',
            border: '1px solid var(--tg-theme-hint-color)',
            borderRadius: '12px',
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            color: 'var(--tg-theme-text-color)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Direction (not for absolute_change) */}
      {formData.type !== 'absolute_change' && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--tg-theme-text-color)',
            }}
          >
            Direction
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['above', 'below'] as SignalDirection[]).map((dir) => (
              <button
                key={dir}
                type="button"
                onClick={() => toggleDirection(dir)}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '15px',
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  backgroundColor: formData.direction.includes(dir)
                    ? dir === 'above'
                      ? 'rgba(52, 199, 89, 0.2)'
                      : 'rgba(255, 59, 48, 0.2)'
                    : 'var(--tg-theme-secondary-bg-color)',
                  color: formData.direction.includes(dir)
                    ? dir === 'above'
                      ? '#34c759'
                      : '#ff3b30'
                    : 'var(--tg-theme-text-color)',
                }}
              >
                {DIRECTION_LABELS[dir]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Repeat Toggle */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          padding: '12px 16px',
          backgroundColor: 'var(--tg-theme-secondary-bg-color)',
          borderRadius: '12px',
        }}
      >
        <div>
          <div style={{ fontWeight: 500, color: 'var(--tg-theme-text-color)' }}>Repeat Alert</div>
          <div style={{ fontSize: '13px', color: 'var(--tg-theme-hint-color)' }}>
            Keep alerting after trigger
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setFormData((prev) => ({ ...prev, repeat: !prev.repeat }));
            impactOccurred('light');
          }}
          style={{
            width: '51px',
            height: '31px',
            borderRadius: '16px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: formData.repeat ? '#34c759' : 'var(--tg-theme-hint-color)',
            position: 'relative',
            transition: 'background-color 0.2s',
          }}
        >
          <div
            style={{
              width: '27px',
              height: '27px',
              borderRadius: '50%',
              backgroundColor: 'white',
              position: 'absolute',
              top: '2px',
              left: formData.repeat ? '22px' : '2px',
              transition: 'left 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          />
        </button>
      </div>

      {/* Error */}
      {error && (
        <p
          style={{
            color: '#ff3b30',
            fontSize: '14px',
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          {error}
        </p>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || !formData.asset || !formData.value}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '17px',
          fontWeight: 600,
          backgroundColor:
            isLoading || !formData.asset || !formData.value
              ? 'var(--tg-theme-hint-color)'
              : 'var(--tg-theme-button-color)',
          color: 'var(--tg-theme-button-text-color)',
          border: 'none',
          borderRadius: '12px',
          cursor: isLoading || !formData.asset || !formData.value ? 'not-allowed' : 'pointer',
        }}
      >
        {isLoading ? 'Saving...' : submitLabel}
      </button>
    </form>
  );
};
